/**
 * CCC Bonus Bundle (free gift with purchase).
 *
 * Pairs with blocks/custom-bonus-bundle.liquid. Three jobs:
 *
 *   1. Keep the gift swatches in step with the qualifying product. Pick a
 *      black machine and the black cup set selects itself, as long as the
 *      customer has not already chosen a colour by hand. Once they have,
 *      their choice wins and we stop touching that gift.
 *   2. Add the chosen gift variants to the cart after the machine lands
 *      there, and keep the stated value in step with the current selection.
 *   3. Show that it worked, without claiming it before the cart agrees.
 *
 * The gifts go to $0 through a Shopify automatic Buy X Get Y discount, not
 * through anything here. This file only ever adds normally-priced line
 * items. If the discount is off, the customer sees them at full price in the
 * cart, a loud failure rather than a quiet one.
 *
 * A gift the customer deliberately removes from the cart is not re-added: we
 * only add gifts on the machine-add that we just watched happen, never on an
 * arbitrary cart refresh.
 */

const BONUS_PROP_KEY = '_bonus_of';
const BONUS_PROP_LABEL = 'Free gift';

class CccBonusBundle extends HTMLElement {
  connectedCallback() {
    if (this.dataset.cccBonusReady === 'true') return;
    this.dataset.cccBonusReady = 'true';

    this.productId = String(this.dataset.productId || '');
    this.currency = this.dataset.currency || 'AUD';
    this.machineVariantIds = new Set(String(this.dataset.variantIds || '').split(',').filter(Boolean));
    this.tiles = Array.from(this.querySelectorAll('[data-ccc-bonus-tile]'));
    this.totalEl = this.querySelector('[data-ccc-bonus-total]');
    this.stateEl = this.querySelector('[data-ccc-bonus-state]');

    /** Gifts the customer has picked a variant for by hand, left alone after that. */
    this.pinned = new Set();
    /** True between the machine's add-to-cart submit and the cart confirming it. */
    this.awaitingAdd = false;
    this.busy = false;

    this.captureMoneyShape();

    this.onChoiceChange = this.onChoiceChange.bind(this);
    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    this.addEventListener('change', this.onChoiceChange);
    document.addEventListener('variant:update', this.onVariantUpdate);
    document.addEventListener('submit', this.onFormSubmit, true);
    document.addEventListener('cart:update', this.onCartUpdate);

    this.syncCartState();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:update', this.onVariantUpdate);
    document.removeEventListener('submit', this.onFormSubmit, true);
    document.removeEventListener('cart:update', this.onCartUpdate);
  }

  /* ------------------------------------------------------------ selection */

  /** @returns {{tile: HTMLElement, variantId: string, price: number}[]} */
  selection() {
    return this.tiles
      .map((tile) => {
        const checked = tile.querySelector('input[type="radio"]:checked');
        // A gift with no swatches carries its single variant on the tile.
        const variantId = checked ? checked.value : tile.dataset.variantId;
        const price = checked ? Number(checked.dataset.price) : Number(tile.dataset.price);
        return variantId ? { tile, variantId: String(variantId), price: price || 0 } : null;
      })
      .filter(Boolean);
  }

  onChoiceChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const tile = input.closest('[data-ccc-bonus-tile]');
    if (!tile) return;

    // Hand-picked from here on. Machine colour changes stop overriding it.
    this.pinned.add(tile.dataset.productId);
    tile.dataset.variantId = input.value;
    tile.dataset.price = input.dataset.price || '0';

    const was = tile.querySelector('[data-ccc-bonus-was]');
    if (was && input.dataset.priceFormatted) was.textContent = input.dataset.priceFormatted;

    this.paintTileImage(tile, input);
    this.paintTotal();
  }

  /**
   * Show the picked variant's own photo. srcset has to be cleared as well as
   * src: image_tag emits a responsive srcset, and the browser keeps honouring
   * that over a freshly assigned src, so the old colour stays on screen.
   */
  paintTileImage(tile, input) {
    const url = input.dataset.image;
    if (!url) return;

    const img = tile.querySelector('.ccc-bonus__img');
    if (!(img instanceof HTMLImageElement)) return;
    if (img.src === url) return;

    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = url;
  }

  /**
   * The qualifying product changed variant. Re-point every un-pinned gift at
   * the matching option value, skipping any that is sold out.
   */
  onVariantUpdate(event) {
    const resource = event?.detail?.resource;
    if (!resource) return;
    if (String(event?.detail?.data?.productId ?? this.productId) !== this.productId) return;

    const wanted = (resource.options || [])
      .filter(Boolean)
      .map((value) => String(value).trim().toLowerCase());
    if (!wanted.length) return;

    for (const tile of this.tiles) {
      if (this.pinned.has(tile.dataset.productId)) continue;

      const options = Array.from(tile.querySelectorAll('input[type="radio"]'));
      const match = options.find(
        (input) =>
          !input.disabled && wanted.includes(String(input.dataset.optionValue || '').trim().toLowerCase())
      );
      if (!match || match.checked) continue;

      match.checked = true;
      tile.dataset.variantId = match.value;
      tile.dataset.price = match.dataset.price || '0';

      const was = tile.querySelector('[data-ccc-bonus-was]');
      if (was && match.dataset.priceFormatted) was.textContent = match.dataset.priceFormatted;

      this.paintTileImage(tile, match);
    }

    this.paintTotal();
  }

  /**
   * Money has to match what Liquid already painted on the page. Intl's
   * currency style disagrees with the shop's money format (it renders
   * A$259.95 against Liquid's $259.95), so the shape is lifted from the
   * server-rendered total instead and only the number is recomputed.
   */
  captureMoneyShape() {
    const sample = this.totalEl ? this.totalEl.textContent.trim() : '';
    const match = sample.match(/^(\D*)[\d.,\s]+(\D*)$/);
    this.moneyPrefix = match ? match[1] : '$';
    this.moneySuffix = match ? match[2] : '';
  }

  formatMoney(cents) {
    let number;
    try {
      number = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(cents / 100);
    } catch {
      number = (cents / 100).toFixed(2);
    }
    return `${this.moneyPrefix}${number}${this.moneySuffix}`;
  }

  paintTotal() {
    if (!this.totalEl) return;
    const cents = this.selection().reduce((sum, entry) => sum + entry.price, 0);
    this.totalEl.textContent = this.formatMoney(cents);
  }

  /* ----------------------------------------------------------------- cart */

  /**
   * Watch the machine's own add-to-cart. We do not intercept it: the theme
   * adds the machine with all its normal error handling and drawer opening,
   * and we add the gifts once the cart confirms the machine arrived. That
   * ordering also puts the machine above its gifts in the cart.
   */
  onFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.action || !form.action.includes('/cart/add')) return;

    // Ownership by variant id is the reliable test: a product page can host
    // several add-to-cart forms (upsells, complementary products), and only
    // the one submitting one of THIS product's variants should trigger gifts.
    const idField = form.querySelector('[name="id"]');
    const submitted = idField ? String(idField.value) : '';
    if (!submitted || !this.machineVariantIds.has(submitted)) return;

    this.awaitingAdd = true;
    this.waitForMachineThenAddGifts();
  }

  /**
   * Horizon does not dispatch cart:update on document from a product page, so
   * waiting on a theme event never fired. Polling the cart itself is dull but
   * it depends on nothing the theme might rename: keep checking until the
   * machine actually lands, then add the gifts behind it.
   */
  async waitForMachineThenAddGifts() {
    if (this.busy) return;
    this.busy = true;

    try {
      const deadline = Date.now() + 10000;
      let cart = null;

      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 350));
        cart = await this.fetchCart();
        if (cart && cart.items.some((line) => String(line.product_id) === this.productId)) break;
        cart = null;
      }

      if (!cart) return;

      const present = new Set(
        cart.items
          .filter((line) => String(line.properties?.[BONUS_PROP_KEY] || '') === this.productId)
          .map((line) => String(line.variant_id))
      );

      const missing = this.selection().filter((entry) => !present.has(entry.variantId));
      if (missing.length) await this.addGifts(missing);

      await this.syncCartState();
    } catch (error) {
      console.warn('[ccc-bonus-bundle] could not add the free gifts:', error);
    } finally {
      this.awaitingAdd = false;
      this.busy = false;
    }
  }

  /**
   * Adding is driven by the submit hook above, not by this event. A cart
   * change the customer made themselves (removing a gift, say) must not
   * silently put it back. This only repaints the tick.
   */
  onCartUpdate() {
    if (this.busy) return;
    this.syncCartState();
  }

  async addGifts(entries) {
    const body = {
      items: entries.map((entry) => ({
        id: Number(entry.variantId),
        quantity: 1,
        properties: {
          [BONUS_PROP_KEY]: this.productId,
          [BONUS_PROP_LABEL]: `With ${this.dataset.productTitle || 'your machine'}`,
        },
      })),
    };

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) throw new Error(`cart/add returned ${response.status}`);

    await this.refreshCartUi();
  }

  /**
   * Re-render whatever cart surface is on the page. Dispatching cart:update
   * here would re-enter onCartUpdate, so this only asks the cart components
   * to refetch. awaitingAdd is already false by now, which makes that pass
   * a harmless state sync anyway.
   */
  async refreshCartUi() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems && typeof cartItems.fetchCartData === 'function') {
      await cartItems.fetchCartData();
    }
    document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: { source: 'ccc-bonus-bundle' } }));
  }

  async fetchCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return response.ok ? response.json() : null;
  }

  /** Only claim "added" when the cart actually holds every chosen gift. */
  async syncCartState() {
    try {
      const cart = await this.fetchCart();
      if (!cart) return;

      const present = new Set(
        cart.items
          .filter((line) => String(line.properties?.[BONUS_PROP_KEY] || '') === this.productId)
          .map((line) => String(line.variant_id))
      );

      const chosen = this.selection();
      const complete = chosen.length > 0 && chosen.every((entry) => present.has(entry.variantId));

      this.classList.toggle('is-in-cart', complete);
      if (this.stateEl) this.stateEl.hidden = !complete;
    } catch {
      /* A cart we cannot read just means no tick. Nothing to recover. */
    }
  }
}

if (!customElements.get('ccc-bonus-bundle')) {
  customElements.define('ccc-bonus-bundle', CccBonusBundle);
}
